"use strict";

import axios from "axios";

import {
    QA_REVIEW_RETURN,
    QA_REVIEW_START,
    QA_REVIEW_SUBMIT,
    QA_REVIEWS,
    QA_TIME_ACTIVITY,
    QA_TIME_CONFIG,
    QA_TIME_CONFIRM,
    QA_TIME_HEARTBEAT,
    QA_TIME_HOURS,
    QA_TIME_RATES,
    QA_TIME_TIMESHEETS,
} from "../../ApiEndpoints";
import { getFirebaseIdToken } from "../auth/api";
import {
    getWithFirebaseAuth,
    postWithFirebaseAuth,
} from "../../utils/firebaseApiClient";

// The backend identifies the reviewer from the Firebase token.
export const startQAReview = (projectId, language) => (
    postWithFirebaseAuth(QA_REVIEW_START, { projectId, language })
);

export const submitQAReview = (projectId, language) => (
    postWithFirebaseAuth(QA_REVIEW_SUBMIT, { projectId, language })
);

export const returnQAReview = ({ projectId, language, userId, note }) => (
    postWithFirebaseAuth(QA_REVIEW_RETURN, { projectId, language, userId, note })
);

export const getQAReviews = (projectId) => (
    getWithFirebaseAuth(`${QA_REVIEWS}/${encodeURIComponent(projectId)}`)
);

// ---- QA time tracking ----

// Resolves to true only when the backend has time tracking on (it answers 404 otherwise).
export const getQATimeConfig = async () => {
    try {
        const response = await getWithFirebaseAuth(QA_TIME_CONFIG);
        return response?.data?.enabled === true;
    } catch (error) {
        return false;
    }
};

export const getQAHours = (projectId, language) => (
    getWithFirebaseAuth(QA_TIME_HOURS, { params: { projectId, language } })
);

// `adjustedHours` and `reason` only when the reviewer changed the calculated hours.
export const confirmQATimesheet = ({ projectId, language, adjustedHours, reason }) => (
    postWithFirebaseAuth(QA_TIME_CONFIRM, {
        projectId,
        language,
        ...(adjustedHours === undefined ? {} : { adjustedHours, reason }),
    })
);

export const getQATimesheets = (projectId) => (
    getWithFirebaseAuth(`${QA_TIME_TIMESHEETS}/${encodeURIComponent(projectId)}`)
);

export const approveQATimesheet = (id, paidHours) => (
    postWithFirebaseAuth(
        `${QA_TIME_TIMESHEETS}/${encodeURIComponent(id)}/approve`,
        paidHours === undefined ? {} : { paidHours }
    )
);

export const markQATimesheetNotPayable = (id, reason) => (
    postWithFirebaseAuth(`${QA_TIME_TIMESHEETS}/${encodeURIComponent(id)}/not-payable`, { reason })
);

export const addQATimesheetAdjustment = (id, { hours, reason }) => (
    postWithFirebaseAuth(`${QA_TIME_TIMESHEETS}/${encodeURIComponent(id)}/adjustments`, {
        hours,
        reason,
    })
);

// Admins: one reviewer's sessions and work actions on a review. Without
// `from`/`to` it covers the open cycle (since their last timesheet).
export const getQAActivity = (projectId, { language, userId, from, to }) => (
    getWithFirebaseAuth(`${QA_TIME_ACTIVITY}/${encodeURIComponent(projectId)}`, {
        params: {
            language,
            userId,
            ...(from ? { from } : {}),
            ...(to ? { to } : {}),
        },
    })
);

// Admins: a reviewer's hourly rate from a date, and their rate history.
export const setQARate = ({ userId, hourlyRate, currency, effectiveFrom }) => (
    postWithFirebaseAuth(QA_TIME_RATES, { userId, hourlyRate, currency, effectiveFrom })
);

export const getQARates = (userId) => (
    getWithFirebaseAuth(`${QA_TIME_RATES}/${encodeURIComponent(userId)}`)
);

// `fetch` with keepalive so a heartbeat sent as the tab closes still arrives;
// sendBeacon cannot carry the Authorization header.
export const sendQAHeartbeat = async ({ projectId, language, playing, active }) => {
    const firebaseIdToken = await getFirebaseIdToken();
    return fetch(QA_TIME_HEARTBEAT, {
        method: "POST",
        keepalive: true,
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${firebaseIdToken}`,
        },
        body: JSON.stringify({ projectId, language, playing, active }),
    });
};

// Headers that let the backend attribute a QA Review page save to the reviewer.
export const qaTrackingHeaders = async (projectId, language) => {
    if (!projectId || !language) return {};
    try {
        const firebaseIdToken = await getFirebaseIdToken();
        if (!firebaseIdToken) return {};
        return {
            "X-QA-Auth": firebaseIdToken,
            "X-QA-Project": projectId,
            "X-QA-Language": language,
        };
    } catch (error) {
        return {};
    }
};

// axios.post for a QA Review page save, with the tracking headers merged in.
// The headers never replace the request's own (for example its Authorization).
export const postWithQATracking = async (url, data, config = {}, { projectId, language } = {}) => {
    const trackingHeaders = await qaTrackingHeaders(projectId, language);
    return axios.post(url, data, {
        ...config,
        headers: { ...trackingHeaders, ...(config?.headers || {}) },
    });
};
